import React, { useEffect, useState } from "react";

import {
  Alert,
  Box,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Cancel, RestartAlt, Search } from "@mui/icons-material";

import { HelpBox, VariableSelection } from "@/components/atoms";
import { useAudienceActions, useBuilder, useDialogs } from "@/context";
import { useVariables } from "@/hooks";
import { Variable, VariableGroup } from "@/types";
import { operators, getClientComponent } from "@/utils";

interface QueryGroupProps {
  group: VariableGroup;
  numQueryGroups: number;
}

const QueryGroup: React.FC<QueryGroupProps> = ({ group: queryGroup, numQueryGroups }) => {
  const { variables } = useVariables();
  const { disabled } = useBuilder();
  const audienceActions = useAudienceActions();
  const { openDialog } = useDialogs();
  const [demogWarning, setDemogWarning] = useState<boolean>(false);

  const id = queryGroup.id;
  const selection = queryGroup.variables;

  useEffect(() => {
    if (demographicsCheck(selection) && queryGroup.operator !== 'OR') {
      setDemogWarning(true);
    } else {
      setDemogWarning(false);
    }
  }, [selection, queryGroup.operator]);

  const demographicsCheck = (variables: Variable[]) => {
    // const categories = ['Age Band', 'Income Band']
    // const demogs = variables.filter(x => x.tag_group === 'Demographics' && categories.includes(x.tag_category));
    const demogs = variables.filter(x => x.tag_group === 'Demographics');
    return demogs.some((variable, idx) => demogs.findIndex(v => v.tag_category === variable.tag_category) !== idx);
  }

  const handleRemoveGroup = () => {
    audienceActions.removeGroup(id);
  };

  const handleChangeOperator = (event: React.ChangeEvent<HTMLInputElement>) => {
    audienceActions.setGroupOperator(id, event.target.value);
  };

  const handleReset = () => {
    audienceActions.resetGroup(id);
  };

  const handleVariableSelect = (variables: Variable[]) => {
    audienceActions.setGroupVariables(id, variables);
  };

  const handleVariableDelete = (variable: Variable) => {
    audienceActions.removeVariable(id, variable.tag_id);
  };

  const handleOpenSearch = () => {
    openDialog({
      type: "variableSearch",
      props: {
        queryGroupId: id,
        selectedVariables: selection,
      },
    });
  };

  const ClientVariableSelection = getClientComponent(
    "VariableSelection",
    VariableSelection
  );

  return (
    <Grid
      container
      size={12}
      spacing={2}
      borderRadius={1}
      p={2}
      component={Paper}
    >
      <Grid size={12}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2">Query Group {id}</Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              color="secondary"
              select
              value={queryGroup.operator}
              onChange={handleChangeOperator}
              sx={{ minWidth: "80px" }}
              disabled={disabled}
            >
              {operators.map((operator) => (
                <MenuItem key={operator} value={operator}>
                  {operator}
                </MenuItem>
              ))}
            </TextField>
            <HelpBox title="Search Variables">
              <IconButton onClick={handleOpenSearch} disabled={disabled}>
                <Search />
              </IconButton>
            </HelpBox>
            <HelpBox title="Reset Query Group">
              <IconButton onClick={handleReset} disabled={disabled}>
                <RestartAlt />
              </IconButton>
            </HelpBox>
            {numQueryGroups > 1 && (
              <HelpBox title="Remove Query Group">
                <IconButton onClick={handleRemoveGroup} disabled={disabled}>
                  <Cancel />
                </IconButton>
              </HelpBox>
            )}
          </Stack>
        </Box>
      </Grid>
      {demogWarning && (
        <Grid size={12}>
          <Alert variant="filled" severity="error">
            It looks like you've selected multiple variables from the same Demographic category.
            Please use the 'OR' operator to include them all, or remove the duplicates.
          </Alert>
        </Grid>
      )}
      <ClientVariableSelection
        variables={variables}
        selection={selection}
        onSelect={handleVariableSelect}
        onDelete={handleVariableDelete}
        disabled={disabled}
      />
    </Grid>
  );
};

export default QueryGroup;
